// Kindra Health · Concept B · Human Care Companion · PREVIEW ONLY
//
// Direction: warm, emotional, human. The hero transforms "scatter" into
// a single calm brief — left side shows the mental load (photos of
// reports, scribbled notes, texts to mum, calendar chaos) and the right
// side shows Kindra's quiet, single-page answer. This is editorial
// health-tech: less product chrome, more story, more relief.
//
// Visual personality: oversized serif editorial headline, tall air,
// paper/sage palette, handwritten micro-details, low-key imagery via
// CSS (we do not fake testimonials or use generic stock-photo people).
// Goal: user feels emotionally understood before they understand the
// product.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../services/activation';
import {
  PAPER, NAVY, NAVY_MUTED, NAVY_SOFT, TEAL, TEAL_SOFT, HAIRLINE, SAGE_BG, CORAL,
  ArrowRight, CheckTick,
  ConceptNav, ConceptFooter,
} from './_shared';

// ─── Scatter collage · left half of hero ──────────────────
// Stylised CSS cards, slightly rotated, with subtle shadows — evokes the
// pile of paperwork, notes and reminders a real patient or carer carries.
// No real photos, no fake names. Names + details are neutral sample data.
const ScatterCollage = () => {
  const items = [
    { rotate: -5, top: '0%',   left: '0%',   w: 210, kind: 'report' },
    { rotate: 4,  top: '8%',   left: '38%',  w: 220, kind: 'note'   },
    { rotate: -8, top: '44%',  left: '3%',   w: 200, kind: 'sticky' },
    { rotate: 7,  top: '48%',  left: '42%',  w: 210, kind: 'text'   },
    { rotate: -2, top: '78%',  left: '20%',  w: 230, kind: 'list'   },
  ];
  return (
    <div className="relative w-full h-[520px] md:h-[580px]" aria-hidden data-testid="b-scatter">
      {/* faded red "URGENT" / "confusing" tag */}
      <div
        className="absolute top-[-10px] right-[10%] px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.22em] rounded-full"
        style={{ backgroundColor: 'rgba(224,122,95,0.12)', color: CORAL, transform: 'rotate(-6deg)' }}
      >
        Before Kindra
      </div>
      {items.map((it, i) => (
        <div
          key={i}
          className="absolute rounded-lg p-3 md:p-4 shadow-lg"
          style={{
            top: it.top,
            left: it.left,
            width: it.w,
            transform: `rotate(${it.rotate}deg)`,
            backgroundColor: it.kind === 'sticky' ? '#F5E9A8' : it.kind === 'text' ? '#E3EEF5' : '#FFFFFF',
            border: `1px solid ${HAIRLINE}`,
            boxShadow: '0 18px 30px -18px rgba(14,28,46,0.28)',
          }}
        >
          {it.kind === 'report' && (
            <>
              <p className="font-sans text-[9.5px] uppercase mb-1.5" style={{ color: NAVY_SOFT, letterSpacing: '0.2em' }}>Pathology · 14 Mar</p>
              <div className="space-y-1 font-sans text-[11.5px]" style={{ color: NAVY_MUTED }}>
                <div className="flex justify-between"><span>Ferritin</span><span style={{ color: CORAL, fontWeight: 600 }}>11 low</span></div>
                <div className="flex justify-between"><span>Haemoglobin</span><span>132</span></div>
                <div className="flex justify-between"><span>TSH</span><span>2.4</span></div>
                <div className="flex justify-between"><span>Vit D</span><span>48</span></div>
              </div>
            </>
          )}
          {it.kind === 'note' && (
            <>
              <p className="font-serif text-[13.5px] mb-1" style={{ color: NAVY }}>Specialist said…</p>
              <p className="font-sans text-[11.5px] leading-[1.5] italic" style={{ color: NAVY_MUTED }}>
                "follow up in 6 weeks" — but what was the other thing she mentioned about the thyroid?
              </p>
            </>
          )}
          {it.kind === 'sticky' && (
            <>
              <p className="font-serif text-[13px] leading-tight" style={{ color: NAVY }}>Don't forget:</p>
              <p className="font-sans text-[11.5px] leading-[1.55] mt-1" style={{ color: NAVY }}>
                · Refill Sertraline<br />
                · Bring Mum's med list<br />
                · GP Friday 10:30
              </p>
            </>
          )}
          {it.kind === 'text' && (
            <>
              <p className="font-sans text-[10px] uppercase mb-1" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Message to Mum</p>
              <p className="font-sans text-[11.5px] leading-[1.5]" style={{ color: NAVY }}>
                "Did the endo say to stay on it or stop? I can't find the letter."
              </p>
            </>
          )}
          {it.kind === 'list' && (
            <>
              <p className="font-sans text-[10px] uppercase mb-1.5" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Questions to ask</p>
              <ul className="font-sans text-[11.5px] space-y-0.5" style={{ color: NAVY_MUTED }}>
                <li>? Why ferritin so low</li>
                <li>? B12 check</li>
                <li>? Thyroid retest</li>
                <li>? Is iron tablets ok</li>
                <li className="opacity-50">? ……</li>
              </ul>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Hero ────────────────────────────────────────────────
const HeroB = ({ onStart }) => (
  <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }} data-testid="b-hero">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-20 lg:pt-24 pb-16 md:pb-24 lg:pb-28">
      <div className="max-w-[820px]">
        <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5" style={{ color: TEAL, letterSpacing: '0.26em' }} data-testid="b-hero-eyebrow">
          A calm place for your health
        </p>
        <h1
          className="font-serif leading-[1.0] tracking-[-0.022em] font-normal text-[52px] sm:text-[68px] lg:text-[88px] xl:text-[96px]"
          style={{ color: NAVY }}
          data-testid="b-hero-headline"
        >
          Stop carrying your<br />
          health story <em className="italic">alone.</em>
        </h1>
        <p className="font-sans mt-8 md:mt-10 text-[17px] md:text-[19px] leading-[1.6] max-w-[620px]" style={{ color: NAVY_MUTED }} data-testid="b-hero-subhead">
          Reports you don't fully understand. Notes scribbled on the fridge. Questions you forget the moment the doctor walks in. Kindra quietly carries all of it for you — and makes it useful when it matters.
        </p>
      </div>

      {/* Scatter → brief transformation */}
      <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-6 relative">
          <ScatterCollage />
        </div>

        {/* Arrow + brief */}
        <div className="lg:col-span-6">
          <div className="hidden lg:flex items-center gap-3 mb-6 ml-2">
            <span className="font-sans text-[10.5px] uppercase" style={{ color: TEAL, letterSpacing: '0.28em' }}>With Kindra</span>
            <span className="flex-1 h-px" style={{ backgroundColor: HAIRLINE }} />
          </div>

          <div
            className="relative rounded-2xl p-7 md:p-9"
            style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}`, boxShadow: '0 40px 80px -30px rgba(14,28,46,0.28)' }}
            data-testid="b-brief-card"
          >
            <p className="font-sans text-[10.5px] uppercase mb-2" style={{ color: TEAL, letterSpacing: '0.22em' }}>Your Friday brief</p>
            <p className="font-serif text-[26px] md:text-[30px] leading-tight mb-4" style={{ color: NAVY }}>
              GP · Friday, 10:30am
            </p>
            <p className="font-sans text-[14px] leading-[1.55] mb-5" style={{ color: NAVY_MUTED }}>
              Built quietly from everything you've added to Kindra over the last three weeks.
            </p>

            <div className="space-y-4 pt-5" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
              <div>
                <p className="font-sans text-[10px] uppercase mb-1.5" style={{ color: NAVY_SOFT, letterSpacing: '0.2em' }}>What's going on</p>
                <p className="font-sans text-[14px] leading-[1.55]" style={{ color: NAVY }}>Fatigue worsening over 3 weeks · recent low ferritin · headaches</p>
              </div>
              <div>
                <p className="font-sans text-[10px] uppercase mb-1.5" style={{ color: NAVY_SOFT, letterSpacing: '0.2em' }}>Questions worth asking</p>
                <ul className="space-y-1 font-sans text-[14px]" style={{ color: NAVY }}>
                  <li><span style={{ color: TEAL }}>?</span> What could be contributing to the low ferritin?</li>
                  <li><span style={{ color: TEAL }}>?</span> Should B12 and thyroid be checked now?</li>
                  <li><span style={{ color: TEAL }}>?</span> When should I follow up?</li>
                </ul>
              </div>
              <div>
                <p className="font-sans text-[10px] uppercase mb-1.5" style={{ color: NAVY_SOFT, letterSpacing: '0.2em' }}>Current medications</p>
                <p className="font-sans text-[14px]" style={{ color: NAVY_MUTED }}>Sertraline 50mg daily</p>
              </div>
            </div>

            <p className="mt-6 pt-4 font-sans italic text-[12.5px]" style={{ color: NAVY_SOFT, borderTop: `1px solid ${HAIRLINE}` }}>
              Yours to edit. Print it, share it, or pull it up on your phone in the waiting room.
            </p>
          </div>
        </div>
      </div>

      {/* CTA row */}
      <div className="mt-14 md:mt-16 flex flex-wrap items-center gap-5">
        <button
          onClick={onStart}
          className="group inline-flex items-center gap-2 rounded-full px-9 py-5 text-[16px] md:text-[17px] font-medium font-sans transition-all hover:translate-y-[-1px]"
          style={{ backgroundColor: NAVY, color: PAPER, boxShadow: '0 14px 34px -14px rgba(14,28,46,0.4)' }}
          data-testid="b-hero-cta"
        >
          Let Kindra hold it with you
          <ArrowRight size={16} stroke={2} />
        </button>
        <span className="font-sans text-[13px]" style={{ color: NAVY_SOFT }}>
          Free for 30 days · no card · private by design
        </span>
      </div>
    </div>
  </section>
);

// ─── The weight · first scroll ───────────────────────────
const WeightSection = () => {
  const lines = [
    'Doctors who start from zero, every time.',
    'Results that arrive without an explanation.',
    'A parent whose medication list lives in five different heads.',
    'Questions that only appear on the drive home.',
  ];
  return (
    <section className="relative" style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }} data-testid="b-weight">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="max-w-[780px]">
          <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5" style={{ color: TEAL, letterSpacing: '0.26em' }}>
            The weight between appointments
          </p>
          <h2 className="font-serif text-[38px] md:text-[56px] lg:text-[68px] leading-[1.04] tracking-[-0.02em] font-normal" style={{ color: NAVY }} data-testid="b-weight-headline">
            You're not imagining it.<br />
            <em className="italic">It really is this much.</em>
          </h2>
          <ul className="mt-12 md:mt-16 space-y-5 md:space-y-7 max-w-[720px]">
            {lines.map((l, i) => (
              <li key={i} className="flex items-start gap-5" data-testid={`b-weight-${i}`}>
                <span
                  className="font-serif text-[28px] md:text-[34px] leading-none flex-shrink-0 mt-1"
                  style={{ color: 'rgba(14,28,46,0.18)' }}
                  aria-hidden
                >
                  {`0${i + 1}`}
                </span>
                <p className="font-serif text-[22px] md:text-[28px] leading-[1.35]" style={{ color: NAVY }}>
                  {l}
                </p>
              </li>
            ))}
          </ul>
          <p className="mt-14 md:mt-20 font-serif italic text-[20px] md:text-[24px] max-w-[620px] leading-[1.5]" style={{ color: NAVY_MUTED }}>
            Kindra exists so you don't hold all of it alone.
          </p>
        </div>
      </div>
    </section>
  );
};

// ─── Product proof · three quiet moments ─────────────────
const ProofB = () => {
  const moments = [
    {
      tag: 'A quiet Tuesday night',
      quote: "My blood results scared me.",
      response: "I've explained what stands out, what's routine, and what's worth raising with your GP.",
    },
    {
      tag: 'Caring for Mum',
      quote: "Mum's cardiologist wants her medication list Tuesday.",
      response: "Her seven current medications and two recent changes — ready on one page, with doses and times.",
    },
    {
      tag: 'The drive home',
      quote: "I forgot everything the specialist said.",
      response: "I saved the key points, next steps and follow-up timing to your record so it's easy to revisit.",
    },
  ];
  return (
    <section className="relative" style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }} data-testid="b-proof">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="max-w-[720px] mb-14 md:mb-20">
          <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5" style={{ color: TEAL, letterSpacing: '0.26em' }}>
            The moments Kindra earns its place
          </p>
          <h2 className="font-serif text-[36px] md:text-[52px] leading-[1.05] tracking-[-0.015em] font-normal" style={{ color: NAVY }}>
            Quiet help, <em className="italic">exactly when you need it.</em>
          </h2>
        </div>

        <div className="space-y-6">
          {moments.map((m, i) => (
            <article
              key={i}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 rounded-2xl p-7 md:p-10"
              style={{ backgroundColor: SAGE_BG, border: `1px solid ${HAIRLINE}` }}
              data-testid={`b-proof-${i}`}
            >
              <div className="md:col-span-4">
                <p className="font-sans text-[10.5px] uppercase mb-2" style={{ color: TEAL, letterSpacing: '0.22em' }}>{m.tag}</p>
                <p className="font-serif italic text-[22px] md:text-[24px] leading-[1.35]" style={{ color: NAVY }}>"{m.quote}"</p>
              </div>
              <div className="md:col-span-8 md:pl-10" style={{ borderLeft: `1px solid ${HAIRLINE}` }}>
                <p className="font-sans text-[10.5px] uppercase mb-2" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>Kindra</p>
                <p className="font-serif text-[20px] md:text-[24px] leading-[1.45]" style={{ color: NAVY }}>
                  {m.response}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Trust ───────────────────────────────────────────────
const TrustB = () => (
  <section className="relative" style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }} data-testid="b-trust">
    <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24 text-center">
      <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>A promise we keep</p>
      <h2 className="font-serif text-[36px] md:text-[52px] leading-[1.08] tracking-[-0.012em] font-normal" style={{ color: NAVY }}>
        Your story is yours.<br />
        <em className="italic">We just hold it gently.</em>
      </h2>
      <div className="mt-10 md:mt-12 flex flex-wrap items-center justify-center gap-2.5 md:gap-3">
        {[
          'Private by design',
          'Never sold',
          'Never prescribes or diagnoses',
          'Supports your clinician',
          'Built in Australia',
        ].map((l, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-2 rounded-full pl-3.5 pr-5 py-2.5"
            style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
            data-testid={`b-trust-${i}`}
          >
            <CheckTick size={13} />
            <span className="font-sans text-[13px]" style={{ color: NAVY }}>{l}</span>
          </span>
        ))}
      </div>
    </div>
  </section>
);

// ─── Final CTA ───────────────────────────────────────────
const CTAB = ({ onStart }) => (
  <section className="relative" style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }} data-testid="b-cta">
    <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 text-center">
      <h2 className="font-serif text-[44px] md:text-[64px] lg:text-[76px] leading-[1.04] tracking-[-0.02em] font-normal" style={{ color: NAVY }}>
        A gentler way to <em className="italic">hold it all.</em>
      </h2>
      <p className="font-sans mt-7 md:mt-9 text-[17px] md:text-[20px] leading-[1.5] max-w-[580px] mx-auto" style={{ color: NAVY_MUTED }}>
        Thirty days free. No card, no pressure. If Kindra earns a place in your week, it's A$9.99/month after.
      </p>
      <button
        onClick={onStart}
        className="mt-10 md:mt-12 group inline-flex items-center gap-2 rounded-full px-9 md:px-10 py-4 md:py-[20px] text-[15px] md:text-[16px] font-medium font-sans transition-colors"
        style={{ backgroundColor: NAVY, color: PAPER }}
        data-testid="b-cta-button"
      >
        Start free
        <ArrowRight size={16} stroke={2} />
      </button>
      <p className="mt-6 font-sans text-[12.5px]" style={{ color: NAVY_SOFT }}>
        Kindra explains and remembers. It doesn't diagnose, prescribe or replace your clinician.
      </p>
    </div>
  </section>
);

// ─── Page ────────────────────────────────────────────────
const ConceptBPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    try { track('concept_viewed', { concept: 'B' }); } catch { /* noop */ }
  }, []);
  const goStart = () => { try { track('clicked_start_free', { src: 'concept_b' }); } catch {} navigate('/register'); };
  const goLogin = () => navigate('/login');
  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: PAPER, color: NAVY }} data-testid="concept-b-page">
      <ConceptNav onStart={goStart} onLogin={goLogin} user={user} activeConcept="B" />
      <HeroB onStart={goStart} />
      <WeightSection />
      <ProofB />
      <TrustB />
      <CTAB onStart={goStart} />
      <ConceptFooter />
    </div>
  );
};

export default ConceptBPage;
